/**
 * Combination Details tab — card-based variant editor.
 *
 * Lifted out of App.js (was the inline `activeTab === 'combination'` branch
 * plus the renderComboCell closure). Backend, save flow, and the
 * comboFormData / formModel.combination shapes are unchanged.
 *
 * Design spec: docs/superpowers/specs/2026-05-28-combination-tab-redesign-design.md
 *
 * Per Decision 10 in App.js, this module duplicates the COLORS palette inline
 * rather than importing a shared one.
 */
import React from 'react';
import { X } from 'lucide-react';
import { computeDifferentiator, applyBulkFill } from './enrichmentForm';

// Palette mirrored from App.js (Decision 10 — do not extract).
const COLORS = {
  maroon: '#8B1D3C',
  gold: '#D4AF37',
  bg: '#FFFFFF',
  bgLight: '#F9FAFB',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  borderHover: '#D1D5DB',
  pending: '#F59E0B',
  verified: '#10B981',
};

// Additional design-token shades used only by this tab.
const TINT = {
  burgundyTint: '#FBEEF0',
  burgundySoft: '#F5D6DC',
  amber: '#F59E0B',
  amberText: '#92400E',
  amberTint: '#FEF7E6',
  amberLight: '#FFFDF6',
  green: '#15803D',
  greenTint: '#ECFDF5',
};

// ── useDifferentiator: thin useMemo wrapper around the pure helper ──────────
// Not exported; consumed by the default export (and eventually by VariantCard).
function useDifferentiator(combinationFields, comboFormData, designCombinations) {
  return React.useMemo(
    () => computeDifferentiator(combinationFields, comboFormData, designCombinations),
    [combinationFields, comboFormData, designCombinations],
  );
}

// ── VariantSummaryStrip: one-line structural summary ────────────────────────
function VariantSummaryStrip({
  combinationFields, designCombinations,
  varyingKeys, commonKeys, missingCount,
}) {
  const fieldByKey = Object.fromEntries(combinationFields.map(f => [f.fieldKey, f]));
  const labelFor = k => fieldByKey[k]?.label || k;
  const orderedVarying = combinationFields.filter(f => varyingKeys.has(f.fieldKey)).map(f => f.fieldKey);
  const orderedCommon  = combinationFields.filter(f => commonKeys.has(f.fieldKey)).map(f => f.fieldKey);

  const truncate = (keys) => {
    if (keys.length <= 2) return keys.map(labelFor).join(', ');
    return `${keys.slice(0, 2).map(labelFor).join(', ')} +${keys.length - 2} more`;
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border mb-3.5"
      style={{ backgroundColor: COLORS.bgLight, borderColor: COLORS.border, fontSize: 12, color: COLORS.textSecondary }}
    >
      <span>
        <b style={{ color: COLORS.textPrimary, fontWeight: 700 }}>{designCombinations.length}</b> variants
      </span>
      {orderedVarying.length > 0 && (
        <>
          <span aria-hidden="true" style={{ color: COLORS.borderHover }}>·</span>
          <span>Varies by <b style={{ color: COLORS.textPrimary, fontWeight: 600 }}>{truncate(orderedVarying)}</b></span>
        </>
      )}
      {orderedCommon.length > 0 && (
        <>
          <span aria-hidden="true" style={{ color: COLORS.borderHover }}>·</span>
          <span>Common: <b style={{ color: COLORS.textPrimary, fontWeight: 600 }}>{truncate(orderedCommon)}</b></span>
        </>
      )}
      {missingCount > 0 && (
        <>
          <span aria-hidden="true" style={{ color: COLORS.borderHover }}>·</span>
          <span style={{ color: TINT.amberText, fontWeight: 600 }}>{missingCount} missing</span>
        </>
      )}
    </div>
  );
}

// ── BulkFillBar: chips for non-varying attributes; click to overwrite all ───
function BulkFillBar({
  combinationFields, designCombinations,
  comboFormData, setComboFormData,
  varyingKeys, commonKeys,
  attrValues, isRequiredFor,
  onBulkApplied,
}) {
  const [openKey, setOpenKey] = React.useState(null);

  // Single-variant designs have no bulk-fill use case.
  if (designCombinations.length <= 1) return null;

  // Common fields only — varying attributes are excluded by construction so
  // the operator can't accidentally collapse a real per-variant difference.
  const bulkFields = combinationFields.filter(f => commonKeys.has(f.fieldKey));
  if (bulkFields.length === 0) return null;

  const sharedValueFor = (fk) => {
    // Returns the shared raw value if every variant has the same non-empty
    // value; otherwise '' (the "+ set all" empty state).
    const vals = designCombinations.map(c => (comboFormData[c.combinationNumber] || {})[fk] || '');
    const non = vals.filter(v => v !== '' && v !== null && v !== undefined);
    if (non.length === 0) return '';
    return non[0]; // computeDifferentiator already proved they're identical.
  };

  const applyValue = (fk, value) => {
    const before = comboFormData;
    const after = applyBulkFill(comboFormData, designCombinations, fk, value);
    setComboFormData(after);
    setOpenKey(null);
    const label = combinationFields.find(f => f.fieldKey === fk)?.label || fk;
    onBulkApplied({ snapshot: before, message: `${label} set to "${value}" for ${designCombinations.length} variants` });
  };

  const hasVarying = varyingKeys.size > 0;
  const varyingLabels = combinationFields.filter(f => varyingKeys.has(f.fieldKey)).map(f => f.label);

  return (
    <div
      className="px-4 py-3.5 rounded-lg border mb-4"
      style={{ backgroundColor: TINT.burgundyTint, borderColor: TINT.burgundySoft }}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span
          style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLORS.maroon, fontWeight: 700 }}
        >
          Set for all variants
        </span>
        {bulkFields.map(f => {
          const shared = sharedValueFor(f.fieldKey);
          const empty = shared === '';
          const required = isRequiredFor(f.fieldKey);
          const isOpen = openKey === f.fieldKey;
          return (
            <div key={f.fieldKey}>
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : f.fieldKey)}
                className="inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full border bg-white text-[13px]"
                style={{ borderColor: TINT.burgundySoft }}
              >
                <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: COLORS.textSecondary, fontWeight: 600 }}>
                  {f.label}{required && empty ? ' *' : ''}
                </span>
                <span
                  className="px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  style={empty
                    ? { backgroundColor: TINT.amberTint, color: TINT.amberText, border: `1px dashed ${TINT.amber}` }
                    : { backgroundColor: TINT.burgundyTint, color: COLORS.textPrimary }
                  }
                >
                  {empty ? '+ set all' : shared}
                </span>
              </button>
              {isOpen && (
                <BulkPicker
                  field={f}
                  attrValues={attrValues}
                  currentValue={shared}
                  onPick={(v) => applyValue(f.fieldKey, v)}
                  onClose={() => setOpenKey(null)}
                />
              )}
            </div>
          );
        })}
      </div>
      {hasVarying && (
        <div className="mt-2" style={{ fontSize: 11, color: COLORS.maroon, opacity: 0.8 }}>
          {varyingLabels.join(', ')} {varyingLabels.length === 1 ? 'is' : 'are'} hidden here — already varies across variants.
        </div>
      )}
    </div>
  );
}

// Inline picker that pops below a bulk chip. For chip-list attributes shows
// the option list; for text / numeric fields shows a single input. Selecting
// a value calls onPick which immediately overwrites every variant.
function BulkPicker({ field, attrValues, currentValue, onPick, onClose }) {
  const opts = attrValues(field.fieldKey);
  const [draft, setDraft] = React.useState(currentValue || '');

  // Text / numeric fields have no option list — render an input + Apply.
  if (opts.length === 0 || field.widget === 'number') {
    return (
      <div
        className="mt-2 px-3 py-2.5 rounded-lg border inline-flex items-center gap-2"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        <input
          autoFocus
          type={field.widget === 'number' ? 'number' : 'text'}
          step={field.widget === 'number' ? '0.01' : undefined}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={`${field.label} for all`}
          className="px-2 py-1 rounded border text-xs"
          style={{ borderColor: COLORS.border, minWidth: 160 }}
        />
        <button
          type="button"
          onClick={() => onPick(draft)}
          className="px-2.5 py-1 rounded text-xs font-semibold text-white"
          style={{ backgroundColor: COLORS.maroon }}
        >Apply to all</button>
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 rounded text-xs"
          style={{ color: COLORS.textSecondary }}
        >Cancel</button>
      </div>
    );
  }

  // Chip-list attribute (single or multi). Bulk apply always writes one value
  // (a single picked option) — multi-select per-variant remains available
  // inside each card. This keeps bulk semantics unambiguous.
  return (
    <div
      className="mt-2 px-3 py-2.5 rounded-lg border"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      <div className="flex flex-wrap gap-1.5">
        {opts.map(o => (
          <button
            key={o}
            type="button"
            onClick={() => onPick(o)}
            className="px-3 py-1 rounded-full text-xs border"
            style={o === currentValue
              ? { backgroundColor: COLORS.maroon, color: 'white', borderColor: COLORS.maroon }
              : { backgroundColor: COLORS.bg, color: COLORS.textPrimary, borderColor: COLORS.border }
            }
          >{o}</button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="ml-2 px-2 py-1 rounded text-xs"
          style={{ color: COLORS.textSecondary }}
        >Cancel</button>
      </div>
    </div>
  );
}

// ── renderComboCell: moved verbatim from App.js with closure deps as args ───
// `attrValues(fieldKey)` is passed in because it depends on the modal's
// coreAttributes + formData (axis lookups). openComboCell / setOpenComboCell
// stay lifted in App.js so existing behaviour and existing tests aren't broken.
export function renderComboCell({
  value, onChange, f, required, cellKey,
  attrValues, openComboCell, setOpenComboCell,
}) {
  const empty = !value || !String(value).trim();
  const errorBorder = (required && empty) ? TINT.amber : COLORS.border;
  const opts = attrValues(f.fieldKey);

  if (f.widget === 'number') {
    return (
      <input
        type="number"
        step="0.01"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded border text-xs"
        style={{ borderColor: errorBorder, backgroundColor: 'white' }}
      />
    );
  }
  if (opts.length === 0) {
    return (
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded border text-xs"
        style={{ borderColor: errorBorder, backgroundColor: 'white' }}
      />
    );
  }
  if (!f.multiSelect) {
    // Always surface the stored value, even if Configuration's option list
    // doesn't contain it — otherwise the dropdown would silently hide legacy
    // data. The "(not in list)" suffix flags it for normalization.
    const inList = !!value && opts.includes(value);
    return (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-2 py-1.5 rounded border text-xs bg-white"
        style={{ borderColor: errorBorder }}
      >
        <option value="">— Select —</option>
        {value && !inList && (
          <option value={value}>{value} (not in list)</option>
        )}
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  // Multi-select — chips up top, remaining options as inline pickers below
  // when this cell is open. Lives in document flow (no absolute / popover)
  // so it never clips inside the table's horizontal scroller.
  const currentValues = value ? String(value).split(',').map(v => v.trim()).filter(Boolean) : [];
  const remaining = opts.filter(o => !currentValues.includes(o));
  const isOpen = openComboCell === cellKey;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpenComboCell(isOpen ? null : cellKey)}
        className="min-h-[28px] w-full px-1.5 py-1 rounded border bg-white flex flex-wrap gap-1 items-center text-left text-xs"
        style={{ borderColor: errorBorder }}
      >
        {currentValues.map((v) => {
          const removeChip = (e) => {
            e.stopPropagation();
            onChange(currentValues.filter(other => other !== v).join(', '));
          };
          return (
            <span
              key={v}
              className="px-1.5 py-0.5 rounded text-[10px] text-white flex items-center gap-1"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {v}
              <span
                role="button"
                tabIndex={0}
                onClick={removeChip}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    removeChip(e);
                  }
                }}
                aria-label={`Remove ${v}`}
                className="hover:bg-white/20 rounded-full cursor-pointer"
              >
                <X size={9} />
              </span>
            </span>
          );
        })}
        {currentValues.length === 0 && (
          <span className="text-gray-400 text-[10px]">— Select —</span>
        )}
      </button>
      {isOpen && (
        <div className="mt-1 flex flex-wrap gap-1">
          {remaining.length === 0 ? (
            <span className="text-[10px] text-gray-400 px-1.5 py-0.5">All selected</span>
          ) : (
            remaining.map(o => (
              <button
                key={o}
                type="button"
                onClick={() => onChange([...currentValues, o].join(', '))}
                className="flex items-center gap-1 px-2 py-0.5 rounded border bg-gray-50 hover:bg-gray-100 text-[10px] text-gray-700 transition-colors"
                style={{ borderColor: COLORS.border }}
              >
                + {o}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components (defined in subsequent tasks) ────────────────────────────
// VariantSummaryStrip — Task 4
// BulkFillBar — Task 5
// UndoToast — Task 7

// ── VariantCard: one card per combination ───────────────────────────────────
function VariantCard({
  combination, combinationFields, comboFormData, setComboFormData,
  headlineKey, isRequiredFor,
  attrValues, openComboCell, setOpenComboCell,
}) {
  const cn = combination.combinationNumber;
  const combo = comboFormData[cn] || {};
  const setComboField = (field, val) => setComboFormData(prev => ({
    ...prev,
    [cn]: { ...(prev[cn] || {}), [field]: val },
  }));

  // Required-empty count for the status badge + border colour.
  const missingCount = combinationFields.reduce((n, f) => {
    if (!isRequiredFor(f.fieldKey)) return n;
    const v = combo[f.fieldKey];
    const empty = v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
    return empty ? n + 1 : n;
  }, 0);

  const editing = (openComboCell || '').startsWith(`${cn}::`);

  // Border colour priority: editing (burgundy) > has missing (amber) > neutral.
  let borderColor = COLORS.border;
  let backgroundColor = COLORS.bg;
  if (editing) {
    borderColor = COLORS.maroon;
  } else if (missingCount > 0) {
    borderColor = TINT.amber;
    backgroundColor = TINT.amberLight;
  }

  // Identity-column rendering.
  const headlineField = headlineKey ? combinationFields.find(f => f.fieldKey === headlineKey) : null;
  const headlineValue = headlineField ? (combo[headlineField.fieldKey] || '') : '';
  const headlineEmpty = !headlineValue || (Array.isArray(headlineValue) && headlineValue.length === 0);

  return (
    <div
      className="grid gap-5 p-4 rounded-lg border"
      style={{ gridTemplateColumns: '120px 1fr 110px', alignItems: 'center', borderColor, backgroundColor, transition: 'border-color .15s' }}
    >
      {/* Identity */}
      <div>
        {headlineField ? (
          <>
            <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: COLORS.textSecondary, fontWeight: 700, marginBottom: 4 }}>
              {headlineField.label}
            </div>
            <div style={{
              fontSize: 20, lineHeight: 1.1, fontWeight: 700,
              fontFamily: 'Georgia, "Times New Roman", serif',
              letterSpacing: '-0.01em',
              color: headlineEmpty ? TINT.amberText : COLORS.textPrimary,
            }}>
              {headlineEmpty ? '—' : (Array.isArray(headlineValue) ? headlineValue.join(', ') : headlineValue)}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 6, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }}>
              #{cn}
            </div>
          </>
        ) : (
          <div style={{
            fontSize: 16, fontWeight: 600, color: COLORS.textPrimary,
            fontVariantNumeric: 'tabular-nums',
          }}>
            #{cn}
          </div>
        )}
      </div>

      {/* Field cluster */}
      <div className="flex flex-wrap gap-1.5 items-start">
        {combinationFields.map(f => (
          <FieldChip
            key={f.fieldKey}
            field={f}
            value={combo[f.fieldKey] || ''}
            onChange={(v) => setComboField(f.fieldKey, v)}
            required={isRequiredFor(f.fieldKey)}
            cellKey={`${cn}::${f.fieldKey}`}
            attrValues={attrValues}
            openComboCell={openComboCell}
            setOpenComboCell={setOpenComboCell}
          />
        ))}
      </div>

      {/* Status badge */}
      <div className="text-right">
        {missingCount > 0 ? (
          <span
            role="status"
            aria-label={`Combination ${cn}: ${missingCount} required field${missingCount === 1 ? '' : 's'} missing`}
            className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold"
            style={{ backgroundColor: TINT.amberTint, color: TINT.amberText }}
          >
            {missingCount} missing
          </span>
        ) : (
          <span
            role="status"
            aria-label={`Combination ${cn}: all required fields complete`}
            className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold"
            style={{ backgroundColor: TINT.greenTint, color: TINT.green }}
          >
            ✓ complete
          </span>
        )}
      </div>
    </div>
  );
}

// Single chip in a variant card. Label + value live in a pill; clicking opens
// the existing renderComboCell widget inline so editing behaviour stays
// identical to the old table (multi-select picker, dropdown, input).
function FieldChip({
  field, value, onChange, required, cellKey,
  attrValues, openComboCell, setOpenComboCell,
}) {
  const empty = !value || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && value.length === 0);

  // Style — amber when required+empty.
  const chipStyle = (empty && required)
    ? { borderColor: TINT.amber, backgroundColor: TINT.amberTint }
    : { borderColor: COLORS.border, backgroundColor: COLORS.bg };
  const labelStyle = (empty && required)
    ? { backgroundColor: TINT.amberTint, color: TINT.amberText, borderRightColor: TINT.amber }
    : { backgroundColor: COLORS.bgLight, color: COLORS.textSecondary };

  return (
    <div className="flex flex-col" style={{ minWidth: 0 }}>
      <div
        className="inline-flex items-stretch rounded-md border overflow-hidden text-[13px]"
        style={chipStyle}
      >
        <span
          className="px-2.5 py-1.5 flex items-center border-r"
          style={{ ...labelStyle, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}
        >
          {field.label}{required ? ' *' : ''}
        </span>
        <div style={{ minWidth: 140, padding: '2px 6px' }}>
          {renderComboCell({
            value, onChange, f: field, required, cellKey,
            attrValues, openComboCell, setOpenComboCell,
          })}
        </div>
      </div>
    </div>
  );
}

// ── UndoToast: 5-second snapshot restore after a bulk-fill ──────────────────
function UndoToast({ toast, onUndo, onDismiss }) {
  React.useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="mt-3 inline-flex items-center gap-3 px-3.5 py-2.5 rounded-lg"
      style={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
    >
      <span>{toast.message}</span>
      <button
        type="button"
        onClick={onUndo}
        style={{ color: TINT.burgundySoft, fontWeight: 700, cursor: 'pointer', background: 'none', border: 'none' }}
      >Undo</button>
    </div>
  );
}

// ── Default export: CombinationTab ──────────────────────────────────────────
export default function CombinationTab({
  design, combinationFields, comboFormData, setComboFormData,
  isRequiredFor, attrValues,
  openComboCell, setOpenComboCell,
}) {
  const designCombinations = React.useMemo(
    () => design?.combinations || [],
    [design?.combinations],
  );

  const { varyingKeys, commonKeys, headlineKey } = useDifferentiator(
    combinationFields, comboFormData, designCombinations,
  );

  // Total missing-required count across all variants — drives the summary strip.
  const missingCount = React.useMemo(() => {
    let n = 0;
    designCombinations.forEach(c => {
      const row = comboFormData[c.combinationNumber] || {};
      combinationFields.forEach(f => {
        if (!isRequiredFor(f.fieldKey)) return;
        const v = row[f.fieldKey];
        const empty = v === undefined || v === null
          || (typeof v === 'string' && !v.trim())
          || (Array.isArray(v) && v.length === 0);
        if (empty) n += 1;
      });
    });
    return n;
  }, [combinationFields, comboFormData, designCombinations, isRequiredFor]);

  // Card ordering: incomplete first (by combo#), then complete (by combo#).
  const orderedCombinations = React.useMemo(() => {
    const isComplete = (c) => {
      const row = comboFormData[c.combinationNumber] || {};
      return combinationFields.every(f => {
        if (!isRequiredFor(f.fieldKey)) return true;
        const v = row[f.fieldKey];
        return !(v === undefined || v === null
          || (typeof v === 'string' && !v.trim())
          || (Array.isArray(v) && v.length === 0));
      });
    };
    const byCn = (a, b) => String(a.combinationNumber).localeCompare(String(b.combinationNumber), undefined, { numeric: true });
    const incomplete = designCombinations.filter(c => !isComplete(c)).sort(byCn);
    const complete = designCombinations.filter(c => isComplete(c)).sort(byCn);
    return [...incomplete, ...complete];
  }, [combinationFields, comboFormData, designCombinations, isRequiredFor]);

  // Undo state. Bulk-fill stages a snapshot; user edits auto-dismiss the toast.
  const [undoToast, setUndoToast] = React.useState(null);
  const onBulkApplied = React.useCallback(({ snapshot, message }) => {
    setUndoToast({ snapshot, message });
  }, []);
  const dismissToast = React.useCallback(() => setUndoToast(null), []);
  const undoLast = React.useCallback(() => {
    if (!undoToast) return;
    setComboFormData(undoToast.snapshot);
    setUndoToast(null);
  }, [undoToast, setComboFormData]);

  // Wrap setComboFormData so any non-bulk user edit (i.e. a call originating
  // from a card cell) clears the toast — undo should never resurrect
  // intermediate state.
  const setComboFromCard = React.useCallback((updater) => {
    setComboFormData(updater);
    setUndoToast(null);
  }, [setComboFormData]);

  if (designCombinations.length === 0) {
    return (
      <div className="text-xs text-gray-400">
        This design has no combinations to enrich.
      </div>
    );
  }

  return (
    <div>
      <VariantSummaryStrip
        combinationFields={combinationFields}
        designCombinations={designCombinations}
        varyingKeys={varyingKeys}
        commonKeys={commonKeys}
        missingCount={missingCount}
      />
      <BulkFillBar
        combinationFields={combinationFields}
        designCombinations={designCombinations}
        comboFormData={comboFormData}
        setComboFormData={setComboFormData}
        varyingKeys={varyingKeys}
        commonKeys={commonKeys}
        attrValues={attrValues}
        isRequiredFor={isRequiredFor}
        onBulkApplied={onBulkApplied}
      />
      <div className="flex flex-col gap-2.5">
        {orderedCombinations.map(c => (
          <VariantCard
            key={c.combinationNumber}
            combination={c}
            combinationFields={combinationFields}
            comboFormData={comboFormData}
            setComboFormData={setComboFromCard}
            headlineKey={headlineKey}
            isRequiredFor={isRequiredFor}
            attrValues={attrValues}
            openComboCell={openComboCell}
            setOpenComboCell={setOpenComboCell}
          />
        ))}
      </div>
      <UndoToast toast={undoToast} onUndo={undoLast} onDismiss={dismissToast} />
    </div>
  );
}
